/**
 * PDKS Kiosk Terminal — Tam ekran, tablet için sürekli açık kalır.
 *
 * Rota: /pdks/terminal  (App layout DIŞINDA, sidebar yok)
 *
 * Akış:
 *  1. Tablet ilk açılışta setup ekranı: 32 karakterlik "Terminal Token" girer,
 *     localStorage'a `fucan_pdks_terminal_token` olarak kaydedilir. Token
 *     geçerli olduğunda descriptors ve employee listesi çekilir.
 *  2. Bekleme ekranı: 30 sn'de bir yenilenen dinamik QR (yüz tanıma kaldırıldı).
 *     Personel telefonuyla okutur → /pdks/qr sayfası kendi oturumuyla kaydeder.
 *  3. Yedek PIN yalnız yönetici menüsünden (terminal token doğrulamasıyla).
 *
 * Ham foto sunucuya asla gönderilmez.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { listPending, removePending, markAttempt, pendingCount } from "@/lib/kioskQueue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Camera, LogIn, LogOut, KeyRound, XCircle, CheckCircle2,
  UserCheck, AlertCircle, Loader2, Wifi, WifiOff, Fingerprint, QrCode,
} from "lucide-react";

const TOKEN_KEY = "fucan_pdks_terminal_token";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Terminal-özel axios: X-Terminal-Token header'ıyla /api/pdks/terminal/... çağırır. */
function termClient(token) {
  return axios.create({
    baseURL: `${BACKEND}/api`,
    headers: { "X-Terminal-Token": token || "" },
    timeout: 12000,
  });
}

export default function KioskTerminal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [empMap, setEmpMap] = useState({});          // id -> {name, department, photo_url} (yedek PIN için)
  const [ready, setReady] = useState(false);
  const [qr, setQr] = useState(null);                // {token, refresh_in}
  const [qrError, setQrError] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [status, setStatus] = useState(null);        // yedek PIN sonucu
  const [online, setOnline] = useState(navigator.onLine);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [pending, setPending] = useState(0);
  const holdRef = useRef(null);  // yönetici girişi: logoya 2 sn basılı tutma

  const refreshPending = useCallback(async () => {
    try { setPending(await pendingCount()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshPending(); }, [refreshPending]);

  const saveToken = async () => {
    const t = tokenInput.trim();
    if (!t || !t.includes(":")) { toast.error("Geçersiz token formatı"); return; }
    try {
      const r = await termClient(t).get("/pdks/terminal/employees");
      if (!Array.isArray(r.data)) throw new Error("Beklenmeyen yanıt");
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success("Terminal kaydedildi");
    } catch (e) {
      toast.error("Token geçersiz veya sunucuya ulaşılamadı");
    }
  };

  const unpairToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setTokenInput(""); setReady(false); setQr(null);
  };

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Personel listesi yalnız yedek PIN diyaloğu için
  useEffect(() => {
    if (!token) return;
    termClient(token).get("/pdks/terminal/employees")
      .then((e) => {
        const map = {};
        for (const emp of e.data || []) map[emp.id] = emp;
        setEmpMap(map);
        setReady(true);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          toast.error("Terminal token geçersiz");
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else { setReady(true); }
      });
  }, [token]);

  // Dinamik QR — sunucunun bildirdiği pencere sonunda yenilenir
  useEffect(() => {
    if (!token || !ready) return;
    let timer; let cancelled = false;
    const load = async () => {
      try {
        const { data } = await termClient(token).get("/pdks/terminal/qr");
        if (cancelled) return;
        setQr(data); setQrError(false);
        timer = setTimeout(load, Math.max(1, data.refresh_in) * 1000 + 300);
      } catch {
        if (cancelled) return;
        setQr(null); setQrError(true);
        timer = setTimeout(load, 5000);
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [token, ready]);

  // Offline replay — kuyruktaki (yedek PIN) kayıtları internet gelince gönder
  useEffect(() => {
    if (!token || !online) return;
    let cancelled = false;
    async function replay() {
      const items = await listPending();
      if (!items.length) return;
      for (const it of items) {
        if (cancelled) return;
        try {
          const { data } = await termClient(token).post("/pdks/terminal/stamp", {
            employee_id: it.employee_id, method: it.method || "pin",
            confidence: it.confidence, client_id: it.client_id, client_ts: it.local_ts,
          });
          if (data?.ok || data?.duplicate) await removePending(it.client_id);
          else await markAttempt(it.client_id, data?.message);
        } catch (e) {
          await markAttempt(it.client_id, e?.message);
          break;
        }
      }
      if (!cancelled) refreshPending();
    }
    replay();
    return () => { cancelled = true; };
  }, [token, online, refreshPending]);

  if (!token) {
    return <SetupScreen tokenInput={tokenInput} setTokenInput={setTokenInput} onSave={saveToken} />;
  }
  if (!ready) {
    return (
      <FullScreenMessage>
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto" />
        <div className="text-slate-300 mt-4">Terminal hazırlanıyor...</div>
        <Button onClick={() => window.confirm("Terminal kaydı silinsin mi?") && unpairToken()} variant="ghost" size="sm" className="mt-6 text-slate-500">Terminal kaydını sil</Button>
      </FullScreenMessage>
    );
  }

  const qrUrl = qr ? `${window.location.origin}/pdks/qr?t=${encodeURIComponent(qr.token)}` : "";

  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-100 overflow-hidden select-none" data-testid="kiosk-terminal">
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-4 z-10 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-sm flex items-center justify-center select-none" data-testid="kiosk-admin-hold"
            onPointerDown={() => { holdRef.current = setTimeout(() => setShowAdminDialog(true), 2000); }}
            onPointerUp={() => clearTimeout(holdRef.current)} onPointerLeave={() => clearTimeout(holdRef.current)}
            onContextMenu={(e) => e.preventDefault()}>
            <QrCode className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-500 font-mono">ACUTO 360 · PDKS</div>
            <div className="text-lg font-semibold">Personel Giriş / Çıkış</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs text-slate-400 flex items-center gap-2">
            {online ? <Wifi className="w-4 h-4 text-emerald-400" /> : <WifiOff className="w-4 h-4 text-rose-400" />}
            {pending > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-amber-950 text-amber-300 text-[10px] uppercase tracking-widest border border-amber-800" data-testid="kiosk-queue-badge">
                {pending} bekleyen kayıt
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="absolute inset-0 pt-20 pb-6 px-6 flex flex-col items-center justify-center text-center">
        {status ? (
          <StatusPanel status={status} empMap={empMap} />
        ) : (
          <>
            <div className="font-mono text-6xl font-semibold tracking-widest" data-testid="kiosk-clock">
              {now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div className="text-slate-400 mt-1">{now.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            <div className="mt-6 bg-white p-5 rounded-md shadow-2xl" data-testid="kiosk-qr">
              {qr ? (
                <QRCodeSVG value={qrUrl} size={340} level="M" data-testid="kiosk-qr-code" data-qr-url={qrUrl} />
              ) : (
                <div className="w-[340px] h-[340px] flex items-center justify-center text-slate-500">
                  {qrError ? "QR alınamadı, yeniden deneniyor…" : <Loader2 className="w-10 h-10 animate-spin text-blue-500" />}
                </div>
              )}
            </div>
            <div className="text-2xl font-semibold mt-6" data-testid="kiosk-qr-instruction">Giriş / Çıkış için QR kodu okutun</div>
            <div className="text-slate-500 text-sm mt-2">Telefonunuzun kamerasıyla okutun · Kod her 30 saniyede yenilenir</div>
          </>
        )}
      </div>

      {showPinDialog && (
        <PinLoginDialog
          token={token}
          empMap={empMap}
          onClose={() => setShowPinDialog(false)}
          onStamped={(res) => {
            const ev = res.event;
            setStatus({
              tone: "success", employee: res.employee,
              title: ev.event_type === "check_in" ? "GİRİŞ BAŞARILI" : "ÇIKIŞ BAŞARILI",
              detail: res.time_hhmmss, event: ev,
            });
            setShowPinDialog(false);
            setTimeout(() => setStatus(null), 4000);
          }}
        />
      )}
      {showAdminDialog && (
        <AdminDialog
          token={token}
          onClose={() => setShowAdminDialog(false)}
          onPin={() => { setShowAdminDialog(false); setShowPinDialog(true); }}
          onUnpair={() => { setShowAdminDialog(false); unpairToken(); }}
        />
      )}
    </div>
  );
}

// ---------- Sub-screens ----------

function FullScreenMessage({ children }) {
  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-100 flex items-center justify-center">
      <div className="text-center">{children}</div>
    </div>
  );
}

function SetupScreen({ tokenInput, setTokenInput, onSave }) {
  return (
    <FullScreenMessage>
      <div className="w-[420px] p-8 bg-slate-900/40 border border-slate-800 rounded-md" data-testid="kiosk-setup">
        <div className="w-12 h-12 mx-auto bg-blue-600 rounded-sm flex items-center justify-center mb-4">
          <Fingerprint className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-xl font-semibold">Terminal Kurulumu</h1>
        <p className="text-slate-400 text-sm mt-2 mb-6">
          Sistem yöneticinizden alacağınız <strong>terminal token</strong>'ını girin. Token bu cihaza bir kez kaydedilir.
        </p>
        <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">Terminal Token</Label>
        <Input value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
          className="bg-slate-950 border-slate-800 rounded-sm mt-1 font-mono text-xs"
          placeholder="terminalId:secret" data-testid="kiosk-setup-token" />
        <Button onClick={onSave} className="w-full mt-4 bg-blue-600 hover:bg-blue-500 rounded-sm"
          data-testid="kiosk-setup-save">
          Kaydet ve Terminal'i Başlat
        </Button>
      </div>
    </FullScreenMessage>
  );
}

function StatusPanel({ status, empMap }) {
  const emp = status.employee || (status.event ? empMap[status.event.employee_id] : null);
  const initials = (emp?.name || "?").split(/\s+/).map(w => w[0]).slice(0,2).join("").toUpperCase();

  const tone = {
    idle:    "text-slate-300",
    hint:    "text-amber-300",
    busy:    "text-blue-300",
    warn:    "text-amber-300",
    error:   "text-rose-400",
    success: "text-emerald-300",
  }[status.tone] || "text-slate-200";

  return (
    <div className="w-full">
      {status.tone === "success" && emp ? (
        <div>
          {emp.photo_url ? (
            <img src={emp.photo_url} alt="" className="w-40 h-40 mx-auto rounded-full object-cover ring-4 ring-emerald-500/40 mb-4" />
          ) : (
            <div className="w-40 h-40 mx-auto rounded-full bg-slate-800 ring-4 ring-emerald-500/40 flex items-center justify-center text-4xl font-mono uppercase mb-4">
              {initials}
            </div>
          )}
          <div className="text-3xl font-semibold text-slate-100">{emp.name}</div>
          {emp.department && <div className="text-slate-500 mt-1">{emp.department}</div>}
          <div className={`text-2xl mt-6 font-mono tracking-widest ${tone}`}>
            {status.event?.event_type === "check_in" ? <LogIn className="w-6 h-6 inline mr-2 -mt-1" /> : <LogOut className="w-6 h-6 inline mr-2 -mt-1" />}
            {status.title}
          </div>
          <div className="text-slate-400 text-xl mt-2 font-mono">{status.detail}</div>
        </div>
      ) : (
        <div>
          <div className="mx-auto mb-6">
            {status.tone === "error" ? <XCircle className="w-24 h-24 mx-auto text-rose-500/70" />
             : status.tone === "warn" ? <AlertCircle className="w-24 h-24 mx-auto text-amber-400/70" />
             : status.tone === "busy" ? <Loader2 className="w-24 h-24 mx-auto text-blue-400 animate-spin" />
             : status.tone === "hint" ? <Camera className="w-24 h-24 mx-auto text-amber-400/70" />
             : <UserCheck className="w-24 h-24 mx-auto text-slate-600" />}
          </div>
          <div className={`text-3xl font-semibold ${tone}`}>{status.title}</div>
          {status.detail && <div className="text-slate-400 text-lg mt-3">{status.detail}</div>}
        </div>
      )}
    </div>
  );
}

function PinLoginDialog({ token, empMap, onClose, onStamped }) {
  const [empId, setEmpId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const employees = useMemo(() => Object.values(empMap).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")), [empMap]);

  const submit = async () => {
    if (!empId) { setErr("Personel seçin"); return; }
    if (!pin || pin.length < 4) { setErr("PIN en az 4 rakam"); return; }
    setBusy(true); setErr("");
    try {
      const verify = await termClient(token).post("/pdks/terminal/pin-verify", { employee_id: empId, pin });
      if (!verify.data?.ok) { setErr("PIN hatalı"); setBusy(false); return; }
      const stamp = await termClient(token).post("/pdks/terminal/stamp", { employee_id: empId, method: "pin" });
      if (stamp.data?.ok === false) {
        setErr(stamp.data?.message || "İşlem başarısız"); setBusy(false); return;
      }
      onStamped(stamp.data);
    } catch (e) {
      setErr("İşlem gerçekleşmedi"); setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-slate-800 rounded-md p-6 w-[420px]" data-testid="kiosk-pin-dialog">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold">PIN ile Giriş / Çıkış</h2>
        </div>
        <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">Personel</Label>
        <select value={empId} onChange={(e) => setEmpId(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 rounded-sm h-10 mt-1 text-slate-100 px-2"
          data-testid="kiosk-pin-employee">
          <option value="">— Seçin —</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.department ? ` · ${e.department}` : ""}</option>)}
        </select>
        <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono mt-4 block">PIN</Label>
        <Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          className="bg-slate-950 border-slate-800 rounded-sm mt-1 font-mono text-2xl text-center tracking-widest"
          maxLength={8} data-testid="kiosk-pin-input" />
        {err && <div className="text-rose-400 text-xs mt-2">{err}</div>}
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={busy} className="flex-1 bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="kiosk-pin-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Onayla
          </Button>
        </div>
      </div>
    </div>
  );
}

// Yönetici: PIN sunucuda doğrulanır; işlemler kısa ömürlü yönetici oturumuyla yapılır
function AdminDialog({ token, onClose, onPin, onUnpair }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [sess, setSess] = useState(null);
  const [view, setView] = useState("menu");
  const native = typeof window !== "undefined" ? window.AcutoKiosk : null;
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await termClient(token).post("/pdks/terminal/admin-verify", { pin });
      setSess(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doğrulanamadı");
      setPin("");
    } finally { setBusy(false); }
  };
  const act = async (action) => {
    try {
      await termClient(token).post("/pdks/terminal/admin-action", { admin_session: sess.admin_session, action });
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Yönetici oturumu geçersiz"); setSess(null); return false; }
  };
  // Android uygulaması oturumu sunucuda kendisi doğrular, sonra Lock Task'tan çıkar
  const exitKiosk = () => { if (native) { native.exitKiosk(token, sess.admin_session); onClose(); } };
  const unpair = async () => {
    if (!window.confirm("Terminal bağlantısı bu cihazdan kaldırılsın mı?")) return;
    if (await act("unpair")) onUnpair();
  };
  const showInfo = async () => { if (await act("device_info")) setView("info"); };
  let device = null;
  try { device = native?.getDeviceInfo ? JSON.parse(native.getDeviceInfo()) : null; } catch { device = null; }
  const t = sess?.terminal || {};
  return (
    <div className="fixed inset-0 bg-slate-950/80 flex items-center justify-center z-50" data-testid="kiosk-admin-dialog">
      <div className="bg-slate-900 border border-slate-800 rounded-md p-6 w-[380px]">
        <h2 className="text-lg font-semibold mb-2">Yönetici</h2>
        {!sess ? (
          <>
            <p className="text-slate-400 text-sm mb-4">Yönetici PIN'ini girin.</p>
            <Input type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="kiosk-admin-pin"
              onKeyDown={(e) => e.key === "Enter" && pin.length >= 4 && verify()}
              className="bg-slate-950 border-slate-800 rounded-sm font-mono text-2xl tracking-[0.5em] text-center h-12" placeholder="••••" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-sm" data-testid="kiosk-admin-cancel">İptal</Button>
              <Button data-testid="kiosk-admin-verify" disabled={busy || pin.length < 4} onClick={verify}
                className="flex-1 bg-blue-600 hover:bg-blue-500 rounded-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Doğrula"}</Button>
            </div>
          </>
        ) : view === "info" ? (
          <div className="space-y-1 text-xs font-mono text-slate-300" data-testid="kiosk-device-info">
            <div>Terminal: {t.code || "—"} {t.note ? `· ${t.note}` : ""}</div>
            <div>Son görülme: {t.last_seen_at ? new Date(t.last_seen_at).toLocaleString("tr-TR") : "—"}</div>
            <div>IP: {t.last_ip || "—"}</div>
            <div>PIN tanımlandı: {t.admin_pin_set_at ? new Date(t.admin_pin_set_at).toLocaleString("tr-TR") : "—"}</div>
            <div>Uygulama: {native ? "Android kiosk" : "Tarayıcı (kiosk kilidi yok)"}</div>
            {device && Object.entries(device).map(([k, v]) => <div key={k}>{k}: {String(v)}</div>)}
            <Button variant="outline" onClick={() => setView("menu")} className="w-full rounded-sm mt-3">Geri</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Button onClick={exitKiosk} disabled={!native} className="w-full bg-rose-600 hover:bg-rose-500 rounded-sm" data-testid="kiosk-exit-kiosk-btn">
              {native ? "Kiosk Modundan Çık" : "Kiosk Modundan Çık (yalnız Android kiosk uygulamasında)"}
            </Button>
            <Button onClick={onPin} className="w-full bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="kiosk-pin-btn">
              <KeyRound className="w-4 h-4 mr-2" /> Yedek PIN ile kayıt
            </Button>
            <Button onClick={unpair} variant="outline" className="w-full rounded-sm" data-testid="kiosk-settings-unpair-btn">
              Terminal Ayarları · Cihaz bağlantısını kaldır
            </Button>
            <Button onClick={showInfo} variant="outline" className="w-full rounded-sm" data-testid="kiosk-info-btn">
              Terminal / Cihaz Bilgisi
            </Button>
            <Button variant="outline" onClick={onClose} className="w-full rounded-sm" data-testid="kiosk-admin-close">Kapat</Button>
          </div>
        )}
      </div>
    </div>
  );
}
